"use client";

import { Suspense, useState } from "react";
import Link from "next/link";
import Logo from "../components/Logo";
import { useRouter, useSearchParams } from "next/navigation";
import { supabase } from "@/lib/supabaseClient";
import { getBrowserSiteOrigin } from "@/lib/siteUrl";
import { withLoader } from "@/lib/apiWithLoader";

/** Signup-only: merge `promo` into a path for post-verify redirect (avoids `?a=1?promo=x`). */
function pathWithPromoQuery(path: string, promo: string | null): string {
  if (!promo) return path;
  const normalized = path.startsWith("/") ? path : `/${path}`;
  const q = normalized.indexOf("?");
  const pathname = q === -1 ? normalized : normalized.slice(0, q);
  const search = q === -1 ? "" : normalized.slice(q + 1);
  const params = new URLSearchParams(search);
  params.set("promo", promo);
  return `${pathname}?${params.toString()}`;
}

type FormState = {
  name: string;
  email: string;
  phone: string;
  password: string;
  agreed: boolean;
  promoCode: string;
};

function SignupPageInner() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const redirectTo = searchParams.get("redirect") || "/onboarding";
  const [form, setForm] = useState<FormState>({
    name: "",
    email: "",
    phone: "",
    password: "",
    agreed: false,
    promoCode: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const handleChange = (
    field: keyof FormState,
    value: string | boolean,
  ) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const isValidEmail = (value: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setMessage(null);

    const nameTrimmed = form.name.trim();
    const emailTrimmed = form.email.trim();

    if (!nameTrimmed) {
      setError("Please enter your full name.");
      return;
    }
    if (nameTrimmed.length < 2) {
      setError("Please enter a valid name (at least 2 characters).");
      return;
    }
    if (!emailTrimmed) {
      setError("Please enter your email address.");
      return;
    }
    if (!isValidEmail(emailTrimmed)) {
      setError("Please enter a valid email address (e.g. name@example.com).");
      return;
    }
    if (!form.password) {
      setError("Please enter a password.");
      return;
    }
    if (form.password.length < 8) {
      setError("Password must be at least 8 characters long.");
      return;
    }
    if (!form.agreed) {
      setError("Please agree to the Terms of Use and Privacy Policy to continue.");
      return;
    }

    if (!supabase) {
      setError("Something went wrong on our end. Please try again later.");
      return;
    }

    try {
      setLoading(true);

      // Signup path: validate before account creation; checkout uses the same /api/validate-code separately.
      const promo = form.promoCode.trim().toUpperCase();
      if (promo) {
        const validateRes = await withLoader(
          () =>
            fetch("/api/validate-code", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ code: promo }),
            }),
          { message: "Validating your code..." },
        );
        const validateData = (await validateRes.json()) as { valid?: boolean; error?: string };
        if (!validateData?.valid) {
          setError(validateData?.error ?? "Invalid coupon or referral code.");
          setLoading(false);
          return;
        }
      }

      const postVerifyPath = pathWithPromoQuery(redirectTo, promo || null);
      const pathForEmail = postVerifyPath.startsWith("/")
        ? postVerifyPath
        : `/${postVerifyPath}`;
      const emailRedirectTo =
        typeof window !== "undefined"
          ? new URL(pathForEmail, `${getBrowserSiteOrigin()}/`).toString()
          : undefined;

      const {
        data: { user },
        error: signUpError,
      } = await supabase.auth.signUp({
        email: form.email.trim(),
        password: form.password,
        options: {
          data: {
            full_name: form.name.trim(),
            phone: form.phone.trim() || null,
          },
          emailRedirectTo,
        },
      });

      if (signUpError) {
        const msg = signUpError.message.toLowerCase();
        if (msg.includes("already registered") || msg.includes("exists")) {
          setError("An account with this email already exists. Please log in or use a different email.");
        } else if (msg.includes("invalid email") || msg.includes("valid email")) {
          setError("Please enter a valid email address.");
        } else if (msg.includes("password") && (msg.includes("least") || msg.includes("length"))) {
          setError("Password must be at least 8 characters long.");
        } else if (msg.includes("database error saving new user")) {
          setError("Signup is temporarily unavailable due to a database setup issue. Please run the latest SQL migration script and try again.");
        } else if (msg.includes("signup") && msg.includes("disabled")) {
          setError("Sign up is temporarily disabled. Please try again later or contact support.");
        } else {
          setError(signUpError.message);
        }
        setLoading(false);
        return;
      }

      if (!user) {
        setMessage(
          "Account created! Please check your email inbox (and spam folder) for a verification link to activate your account.",
        );
      } else {
        router.push(
          promo ? pathWithPromoQuery("/onboarding", promo) : redirectTo,
        );
      }
    } catch (cause) {
      setError("We couldn’t create your account right now. Please check your connection and try again.");
      // eslint-disable-next-line no-console
      console.error(cause);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center px-4 py-10 text-slate-100 sm:px-6">
      <div className="absolute left-4 top-6 sm:left-6">
        <Logo href="/" className="opacity-90 hover:opacity-100" />
      </div>
      <div className="w-full max-w-md">
        <div className="mb-6 text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-amber-300">
            Welcome to Aptinomic
          </p>
          <h1 className="mt-2 font-heading text-2xl font-semibold text-slate-50">
            Create your account
          </h1>
          <p className="mt-1 text-xs text-slate-300">
            Start your AI-powered career journey in just a few minutes.
          </p>
        </div>

        <div className="rounded-3xl bg-slate-950/90 p-6 shadow-xl shadow-slate-950/60 ring-1 ring-slate-800">
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="space-y-1.5">
              <label
                htmlFor="name"
                className="text-xs font-medium text-slate-200"
              >
                Full name
              </label>
              <input
                id="name"
                type="text"
                required
                value={form.name}
                onChange={(event) => handleChange("name", event.target.value)}
                className="w-full rounded-2xl border border-slate-700 bg-slate-900/90 px-3 py-2.5 text-sm text-slate-100 outline-none ring-0 transition placeholder:text-slate-500 focus:border-amber-300 focus:ring-1 focus:ring-amber-300"
                placeholder="Aarya Sharma"
              />
            </div>

            <div className="space-y-1.5">
              <label
                htmlFor="email"
                className="text-xs font-medium text-slate-200"
              >
                Email
              </label>
              <input
                id="email"
                type="email"
                required
                value={form.email}
                onChange={(event) => handleChange("email", event.target.value)}
                className="w-full rounded-2xl border border-slate-700 bg-slate-900/90 px-3 py-2.5 text-sm text-slate-100 outline-none ring-0 transition placeholder:text-slate-500 focus:border-amber-300 focus:ring-1 focus:ring-amber-300"
                placeholder="you@example.com"
              />
            </div>

            <div className="space-y-1.5">
              <label
                htmlFor="phone"
                className="text-xs font-medium text-slate-200"
              >
                Phone (optional)
              </label>
              <input
                id="phone"
                type="tel"
                value={form.phone}
                onChange={(event) => handleChange("phone", event.target.value)}
                className="w-full rounded-2xl border border-slate-700 bg-slate-900/90 px-3 py-2.5 text-sm text-slate-100 outline-none ring-0 transition placeholder:text-slate-500 focus:border-amber-300 focus:ring-1 focus:ring-amber-300"
                placeholder="+91 98765 43210"
              />
            </div>

            <div className="space-y-1.5">
              <label
                htmlFor="promo"
                className="text-xs font-medium text-slate-200"
              >
                Coupon or referral code (optional)
              </label>
              <input
                id="promo"
                type="text"
                value={form.promoCode}
                onChange={(event) =>
                  handleChange("promoCode", event.target.value.toUpperCase())
                }
                className="w-full rounded-2xl border border-slate-700 bg-slate-900/90 px-3 py-2.5 text-sm text-slate-100 outline-none ring-0 transition placeholder:text-slate-500 focus:border-amber-300 focus:ring-1 focus:ring-amber-300"
                placeholder="e.g. WELCOME20"
              />
            </div>

            <div className="space-y-1.5">
              <label
                htmlFor="password"
                className="text-xs font-medium text-slate-200"
              >
                Password
              </label>
              <input
                id="password"
                type="password"
                required
                minLength={8}
                value={form.password}
                onChange={(event) =>
                  handleChange("password", event.target.value)
                }
                className="w-full rounded-2xl border border-slate-700 bg-slate-900/90 px-3 py-2.5 text-sm text-slate-100 outline-none ring-0 transition placeholder:text-slate-500 focus:border-amber-300 focus:ring-1 focus:ring-amber-300"
                placeholder="At least 8 characters"
              />
            </div>

            <div className="flex items-start gap-2 rounded-2xl bg-slate-900/80 px-3 py-2.5">
              <input
                id="agreed"
                type="checkbox"
                checked={form.agreed}
                onChange={(event) =>
                  handleChange("agreed", event.target.checked)
                }
                className="mt-0.5 h-4 w-4 rounded border-slate-600 bg-slate-900 text-amber-300 focus:ring-amber-300"
              />
              <label
                htmlFor="agreed"
                className="text-[11px] leading-relaxed text-slate-300"
              >
                I agree to the{" "}
                <Link
                  href="/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-amber-300 underline-offset-2 hover:underline"
                >
                  Terms of Use
                </Link>{" "}
                and{" "}
                <Link
                  href="/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-amber-300 underline-offset-2 hover:underline"
                >
                  Privacy Policy
                </Link>
                .
              </label>
            </div>

            {error && (
              <p className="rounded-2xl bg-red-500/10 px-3 py-2 text-xs text-red-200">
                {error}
              </p>
            )}
            {message && (
              <p className="rounded-2xl bg-emerald-500/10 px-3 py-2 text-xs text-emerald-200">
                {message}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="flex w-full items-center justify-center rounded-full bg-amber-400 px-4 py-2.5 text-sm font-semibold text-slate-950 shadow-md shadow-amber-500/40 transition hover:bg-amber-300 disabled:cursor-not-allowed disabled:bg-amber-300/70"
            >
              {loading ? "Creating your account..." : "Sign up with email"}
            </button>
          </form>

          <p className="mt-4 text-center text-[11px] text-slate-400">
            Already have an account?{" "}
            <Link
              href="/login"
              className="font-semibold text-amber-300 hover:text-amber-200"
            >
              Log in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default function SignupPage() {
  return (
    <Suspense fallback={<div className="min-h-screen bg-slate-950" />}>
      <SignupPageInner />
    </Suspense>
  );
}

